import { format } from "date-fns";
import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, FilePlus2, Loader2, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { DatePicker } from "../../../../components/ui/date-picker";
import { Label } from "../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  useCadastrosNp,
  useCriarNp,
  useDownloadNp,
  useNotasPromissorias,
} from "../../hooks/use-nota-promissoria";
import { CriarNpResult } from "../../types";
import { numeroDocumentoDe, proximosIdContratos } from "../../utils/contrato";
import { TENTATIVAS_DOWNLOAD } from "../../utils/download";
import { formatDateBR } from "../../../../utils/format-utils";
import { formatarDocumento } from "../../utils/documento";
import { documentosProntos, formatarCentavos, hojeSaoPaulo } from "../../utils/vencimento";
import { InputCentavos } from "../../components/input-centavos";

interface LinhaNota {
  chave: number;
  /** centavos, como a coluna bigint e o resto da cadeia */
  valorFace: number;
  valorCompra: number;
  dataVencimento?: Date;
}

/** Tempo máximo aguardando a imagem R gerar CNAB + PDFs antes de desistir do download automático. */
const TIMEOUT_DOCS_MS = 4 * 60 * 1000;
const INTERVALO_POLL_MS = 3000;

/**
 * Etapas do download que fecha a criação. Como nada é enviado por e-mail, o ZIP
 * é o único caminho para os arquivos: uma falha precisa ficar visível e ter um
 * botão para repetir, em vez de sumir num toast.
 */
type StatusDownload = "aguardando" | "baixando" | "concluido" | "falhou";

const isoDe = (d?: Date) => (d ? format(d, "yyyy-MM-dd") : "");

let proximaChave = 1;
const novaLinha = (): LinhaNota => ({ chave: proximaChave++, valorFace: 0, valorCompra: 0 });

export default function NotaPromissoriaCriarPage() {
  const navigate = useNavigate();
  const { data: cadastros, isLoading: carregandoCadastros } = useCadastrosNp();
  const { data: existentes = [] } = useNotasPromissorias("todas");
  const criar = useCriarNp();
  const download = useDownloadNp();

  const [cedenteId, setCedenteId] = useState("");
  const [sacadoId, setSacadoId] = useState("");
  const [linhas, setLinhas] = useState<LinhaNota[]>([novaLinha()]);

  // A emissão é sempre o dia da criação: quem define é o backend, aqui só
  // mostramos e validamos os vencimentos contra ela.
  const dataEmissao = hojeSaoPaulo();
  const [criado, setCriado] = useState<{ resultado: CriarNpResult; em: number } | null>(null);
  const [statusDownload, setStatusDownload] = useState<StatusDownload>("aguardando");

  // Previsão da numeração: quem gera de fato é o backend, ao inserir.
  const idsPrevistos = useMemo(
    () => proximosIdContratos(existentes, linhas.length),
    [existentes, linhas.length]
  );

  // Pré-seleciona quando só há uma opção.
  useEffect(() => {
    if (cadastros?.cedentes.length === 1) setCedenteId(cadastros.cedentes[0].id);
    if (cadastros?.sacados.length === 1) setSacadoId(cadastros.sacados[0].id);
  }, [cadastros]);

  // Acompanha a geração dos documentos das NPs recém-criadas.
  const esperandoDocumentos = criado !== null && statusDownload === "aguardando";
  const dentroDoPrazo = Date.now() - (criado?.em ?? 0) < TIMEOUT_DOCS_MS;
  const { data: notasCriadas = [] } = useNotasPromissorias("todas", {
    ids: criado?.resultado.ids,
    enabled: criado !== null,
    refetchInterval: esperandoDocumentos && dentroDoPrazo ? INTERVALO_POLL_MS : false,
  });
  const todasProntas = notasCriadas.length > 0 && notasCriadas.every(documentosProntos);
  const expirou = esperandoDocumentos && !dentroDoPrazo;

  const baixarDocumentos = () => {
    if (!criado) return;
    setStatusDownload("baixando");
    download.mutate(criado.resultado.ids, {
      onSuccess: () => setStatusDownload("concluido"),
      onError: () => setStatusDownload("falhou"),
    });
  };

  // Assim que a imagem R termina, o download sai sozinho — uma vez só, porque
  // o disparo já tira o estado de "aguardando".
  useEffect(() => {
    if (esperandoDocumentos && todasProntas) baixarDocumentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esperandoDocumentos, todasProntas]);

  const atualizarLinha = (
    chave: number,
    campo: keyof LinhaNota,
    valor: number | Date | undefined
  ) =>
    setLinhas((ls) => ls.map((l) => (l.chave === chave ? { ...l, [campo]: valor } : l)));

  const adicionarLinha = () => setLinhas((ls) => [...ls, novaLinha()]);

  const removerLinha = (chave: number) => setLinhas((ls) => ls.filter((l) => l.chave !== chave));

  const totalFace = linhas.reduce((s, l) => s + l.valorFace, 0);
  const totalCompra = linhas.reduce((s, l) => s + l.valorCompra, 0);

  const linhasValidas = linhas.every(
    (l) =>
      l.valorFace > 0 &&
      l.valorCompra > 0 &&
      l.valorCompra <= l.valorFace &&
      !!l.dataVencimento &&
      isoDe(l.dataVencimento) > dataEmissao
  );
  const canSubmit = !!cedenteId && !!sacadoId && linhas.length > 0 && linhasValidas && !criar.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    criar.mutate(
      {
        cedenteId,
        sacadoId,
        notas: linhas.map((l) => ({
          valorFace: l.valorFace,
          valorCompra: l.valorCompra,
          dataVencimento: isoDe(l.dataVencimento),
        })),
      },
      {
        onSuccess: (resultado) => {
          setCriado({ resultado, em: Date.now() });
          setStatusDownload("aguardando");
        },
      }
    );
  };

  const reiniciar = () => {
    setCriado(null);
    setStatusDownload("aguardando");
    setLinhas([novaLinha()]);
  };

  return (
    <div className="p-6 space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <motion.div
              className="p-2 rounded-lg bg-primary/10"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <FilePlus2 className="h-6 w-6 text-primary" />
            </motion.div>
            <h1 className="text-3xl font-bold text-foreground bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Criar Nota Promissória
            </h1>
          </div>
          <p className="text-muted-foreground mt-1 ml-14">
            Gera o CNAB de compra, o termo de cessão e a NP em PDF. Ao final, baixa o ZIP com os três arquivos.
          </p>
        </div>
      </motion.div>

      {criado ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {statusDownload === "concluido" ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : statusDownload === "falhou" ? (
                <AlertTriangle className="h-4 w-4 text-destructive" />
              ) : (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {statusDownload === "concluido"
                ? "Documentos gerados e baixados"
                : statusDownload === "falhou"
                ? "Documentos gerados, mas o download falhou"
                : statusDownload === "baixando"
                ? "Baixando os documentos..."
                : expirou
                ? "Geração demorando mais que o esperado"
                : "Gerando CNAB de compra, termo de cessão e NP..."}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Emissão em {formatDateBR(criado.resultado.dataEmissao)}.
            </p>
            <ul className="space-y-1 text-sm">
              {criado.resultado.idContratos.map((idContrato) => {
                const np = notasCriadas.find((n) => n.id_contrato === idContrato);
                const item = (ok: boolean | undefined, label: string) => (
                  <span className={ok ? "text-green-700" : "text-muted-foreground"}>
                    {ok ? "✓" : "…"} {label}
                  </span>
                );
                return (
                  <li key={idContrato} className="flex flex-wrap items-center gap-3">
                    <span className="font-mono">{numeroDocumentoDe(idContrato)}</span>
                    {item(!!np?.cnab_remessa_key_path, "CNAB compra")}
                    {item(!!np?.termo_cessao_key_path, "Termo de cessão")}
                    {item(!!np?.termo_np_key_path, "NP")}
                  </li>
                );
              })}
            </ul>
            {expirou && (
              <p className="text-sm text-muted-foreground">
                Verifique os logs da lambda docGenerator. Quando os documentos aparecerem no controle de NP, o download fica
                disponível por lá.
              </p>
            )}

            {statusDownload === "falhou" && (
              <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm">
                <p className="font-medium text-destructive">Os arquivos não foram baixados.</p>
                <p className="text-muted-foreground">
                  Eles estão guardados no S3 e nada é enviado por e-mail, então repita o download aqui ou pelo controle de
                  NP. Já foram feitas {TENTATIVAS_DOWNLOAD} tentativas automáticas.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {statusDownload === "falhou" ? (
                <Button disabled={download.isPending} onClick={baixarDocumentos}>
                  <RotateCcw className={`h-4 w-4 mr-1 ${download.isPending ? "animate-spin" : ""}`} />
                  Tentar novamente
                </Button>
              ) : (
                <Button
                  variant="outline"
                  disabled={!todasProntas || download.isPending}
                  onClick={baixarDocumentos}
                >
                  Baixar ZIP novamente
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate("/notas-promissorias")}>
                Ir para o controle de NP
              </Button>
              <Button variant="outline" onClick={reiniciar}>
                Criar outra NP
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Partes e emissão</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Cedente</Label>
                  <Select value={cedenteId} onValueChange={setCedenteId} disabled={carregandoCadastros}>
                    <SelectTrigger>
                      <SelectValue placeholder={carregandoCadastros ? "Carregando..." : "Selecione o cedente"} />
                    </SelectTrigger>
                    <SelectContent>
                      {cadastros?.cedentes.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.NOME_COMPLETO_RAZAO_SOCIAL} ({formatarDocumento(c.NUMERO_DOCUMENTO)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Sacado (devedor)</Label>
                  <Select value={sacadoId} onValueChange={setSacadoId} disabled={carregandoCadastros}>
                    <SelectTrigger>
                      <SelectValue placeholder={carregandoCadastros ? "Carregando..." : "Selecione o sacado"} />
                    </SelectTrigger>
                    <SelectContent>
                      {cadastros?.sacados.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.NOME_COMPLETO_RAZAO_SOCIAL} ({formatarDocumento(s.NUMERO_DOCUMENTO)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Data de emissão</Label>
                  <div className="flex h-9 items-center rounded-md border border-dashed bg-muted/40 px-3">
                    <span className="text-sm">{formatDateBR(dataEmissao)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Hoje — é a data que vai no CNAB, para ser inputado no portal.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-base">
                <span>Notas</span>
                <span className="text-xs font-normal text-muted-foreground">
                  Numeração automática, na sequência do último contrato cadastrado
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {linhas.map((l, idx) => (
                <div key={l.chave} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1.4fr_auto] gap-3 items-end">
                  <div className="space-y-1">
                    {idx === 0 && <Label>Documento</Label>}
                    <div className="flex h-9 items-center rounded-md border border-dashed bg-muted/40 px-3">
                      <span className="font-mono text-sm">
                        {idsPrevistos[idx] ? numeroDocumentoDe(idsPrevistos[idx]) : "—"}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {idx === 0 && <Label>Valor de face (R$)</Label>}
                    <InputCentavos
                      aria-label="Valor de face"
                      valor={l.valorFace}
                      onChange={(centavos) => atualizarLinha(l.chave, "valorFace", centavos)}
                    />
                  </div>
                  <div className="space-y-1">
                    {idx === 0 && <Label>Valor de compra (R$)</Label>}
                    <InputCentavos
                      aria-label="Valor de compra"
                      valor={l.valorCompra}
                      onChange={(centavos) => atualizarLinha(l.chave, "valorCompra", centavos)}
                    />
                  </div>
                  <div className="space-y-1">
                    {idx === 0 && <Label>Vencimento</Label>}
                    <DatePicker date={l.dataVencimento} setDate={(d) => atualizarLinha(l.chave, "dataVencimento", d)} />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={linhas.length === 1}
                    onClick={() => removerLinha(l.chave)}
                    aria-label="Remover nota"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <div className="flex items-center justify-between pt-2">
                <Button variant="outline" size="sm" onClick={adicionarLinha}>
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar nota
                </Button>
                {totalFace > 0 && (
                  <div className="grid grid-cols-3 gap-4 rounded-md bg-muted/40 px-4 py-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Face total</p>
                      <p className="font-semibold text-primary">{formatarCentavos(totalFace)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Compra total</p>
                      <p className="font-medium">{formatarCentavos(totalCompra)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Deságio</p>
                      <p className="font-medium text-yellow-600">{formatarCentavos(totalFace - totalCompra)}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {criar.isPending ? "Cadastrando..." : `Criar ${linhas.length > 1 ? `${linhas.length} NPs` : "NP"} e gerar documentos`}
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
